/**
 * ----------------------------------------------------------------------------------------------------------------------
 * MEDIA REVEAL
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Reveals every [reveal-media] element as it scrolls into the viewport, by flipping the [reveal-media-visible] attribute
 * that "media-reveal.css" animates. Elements entering during the same observer callback are staggered slightly so that a
 * row of images cascades instead of appearing all at once.
 */

(() => {
  const ROOT_CLASS = 'media-reveal-enabled';
  const SELECTOR = '[reveal-media], .reveal-media';
  const VISIBLE_ATTRIBUTE = 'reveal-media-visible';
  const STAGGER_MS = 70;
  const MAX_STAGGER_STEPS = 5;

  const root = document.documentElement;

  // The layout only adds this class when the reveal is enabled for the current page.
  if (!root.classList.contains(ROOT_CLASS)) {
    return;
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.classList.remove(ROOT_CLASS);
    return;
  }

  // Tells the layout failsafe that the module booted, so it leaves the root class in place.
  root.setAttribute('media-reveal-ready', '');

  const observed = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);

      // IntersectionObserver does not guarantee entry order, so we sort top-to-bottom then start-to-end to make the
      // stagger follow the reading order of the page.
      visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      visible.forEach((entry, index) => {
        const element = entry.target;

        observer.unobserve(element);

        if (index > 0) {
          element.style.setProperty('--media-reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
        }

        element.setAttribute(VISIBLE_ATTRIBUTE, '');
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  const observe = (element) => {
    if (observed.has(element) || element.hasAttribute(VISIBLE_ATTRIBUTE)) {
      return;
    }

    observed.add(element);
    observer.observe(element);
  };

  const scan = (scope = document) => {
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches(SELECTOR)) {
      observe(scope);
    }

    scope.querySelectorAll(SELECTOR).forEach(observe);
  };

  scan();

  // Content injected after load (section rendering API, quick buy, infinite scroll, …) still needs to be picked up. Only
  // the added subtrees are scanned, as the theme mutates the DOM often and re-scanning the whole document is wasteful.
  const pendingScopes = new Set();
  let scanRequestId = null;

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          pendingScopes.add(node);
        }
      });
    });

    if (pendingScopes.size === 0 || scanRequestId !== null) {
      return;
    }

    scanRequestId = requestAnimationFrame(() => {
      scanRequestId = null;
      pendingScopes.forEach(scan);
      pendingScopes.clear();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Theme editor: sections are swapped out wholesale, so re-scan the new markup.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
